import React, { useState } from "react";

import { Icon } from "components/icon/Icon";
import AutoCompleteInput from "common/autoComplete/components/AutoCompleteInput";
import { UsersAPI } from "api/users";
import Regexp from "common/Regexp";
import { captureEvent } from "components/posthog/customEventCapture";
import { User, UserForm } from "models/admin/admin";

import "./EditUser.scss";

type Props = {
  isSaving: boolean;
  handleItemConfirm: (request: UserForm) => void;
};

type FormValidity = {
  email: boolean;
  givenName: boolean;
  socialSecurityNumber: boolean;
  surName: boolean;
};

export default function EditUser({
  isSaving,
  handleItemConfirm: onConfirm
}: Props) {
  const [currentUser, setCurrentUser] = useState<UserForm>(emptyUser());
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<string[][]>(
    []
  );
  const [autocompleteUsers, setAutocompleteUsers] = useState<User[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [initialEmail, setInitialEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formValidity, setFormValidity] =
    useState<FormValidity>(resetFormValidity());
  const [shouldCheckEmail, setShouldCheckEmail] = useState(false);

  function emptyUser(): UserForm {
    return {
      externalUserId: "",
      givenName: "",
      surName: "",
      socialSecurityNumber: "",
      email: ""
    };
  }

  function resetFormValidity() {
    return {
      email: true,
      givenName: true,
      socialSecurityNumber: true,
      surName: true
    };
  }

  const isFormValid = () => {
    return Object.values(formValidity).every(Boolean);
  };

  const handleOnChangeExternal = (
    _: React.ChangeEvent<HTMLInputElement>,
    emailPrefix: string
  ) => {
    if (!emailPrefix) {
      return;
    }
    if (emailPrefix.length < 3) {
      setAutoCompleteOptions([]);
      setAutocompleteUsers([]);
      return;
    }
    UsersAPI.adminSearchExternalUsers(emailPrefix).then((result) => {
      const items = JSON.parse(result.text).items;
      setAutocompleteUsers(items);
      setAutoCompleteOptions(items.map((i: User) => [i.givenName, i.email]));
    });
  };

  const handleUserSelected = (index: number) => {
    const selectedUser = autocompleteUsers[index];
    const selectedUserForm = {
      externalUserId: selectedUser.id ?? "",
      givenName: selectedUser.givenName,
      surName: selectedUser.surName,
      socialSecurityNumber: selectedUser.socialSecurityNumber,
      email: selectedUser.email
    };
    setCurrentUser(selectedUserForm);
    Object.entries(selectedUserForm).forEach(([name, value]) => {
      if (name !== "externalUserId") {
        setFormValidity((prev) => ({
          ...prev,
          [name]: validateField(name, value)
        }));
      }
    });
    setShouldCheckEmail(false);
    setIsEditing(true);
    setInitialEmail(selectedUser.email);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentUser((prev) => ({ ...prev, [name]: value }));
    setShouldCheckEmail(value !== initialEmail);
    setFormValidity((prev) => ({
      ...prev,
      [name]: validateField(name, value)
    }));
  };

  const validateField = (name: string, value: string) => {
    switch (name) {
      case "email":
        return Regexp.email.test(value);
      case "socialSecurityNumber":
        return Regexp.ssn.test(value);
      case "givenName":
      case "surName":
        return value.trim().length > 0;
      default:
        return false;
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(currentUser);
    setShowSuccess(true);
  };

  const validateEmail = async (
    e: React.FocusEvent<HTMLInputElement, Element>
  ) => {
    if (!shouldCheckEmail || !formValidity.email) {
      return;
    }
    const email = e.target.value;
    if (!email) {
      setFormValidity((prev) => ({
        ...prev,
        email: false
      }));
      return;
    }

    setIsLoading(true);
    try {
      const result = await UsersAPI.searchExternalUsers(email);
      let externalUsers: User[] = [];
      try {
        externalUsers = JSON.parse(result.text).items || [];
      } catch (err) {
        console.error("Error parsing response:", err);
      }

      setFormValidity((prev) => ({
        ...prev,
        email: !externalUsers.some((user) => user.email === email)
      }));
    } catch (err) {
      console.error("Failed to fetch emails");
    } finally {
      setIsLoading(false);
    }
  };

  const getInputClassName = (fieldName: keyof FormValidity) => {
    return formValidity[fieldName] ? "" : "invalid";
  };

  const saveSuccess = () => {
    return showSuccess && !isSaving ? (
      <Icon name="check" size="2x" className="success" />
    ) : (
      ""
    );
  };

  const formFields: { name: keyof UserForm; label: string }[][] = [
    [
      { name: "email", label: "E-post" },
      { name: "socialSecurityNumber", label: "Personnummer" }
    ],

    [
      { name: "givenName", label: "Förnamn" },
      { name: "surName", label: "Efternamn" }
    ]
  ];

  return (
    <div className="findUserWrapper">
      <div className="userWrapper">
        <AutoCompleteInput
          selectKey={1}
          options={autoCompleteOptions}
          onChange={handleOnChangeExternal}
          onUserSelected={handleUserSelected}
          placeholder="Sök på kundanvändares e-post"
        />
      </div>

      {isEditing && (
        <form onSubmit={handleFormSubmit} className="editUserForm">
          <div className="editUserWrapper">
            {formFields.map((column, index) => (
              <div key={`column-${index}`}>
                {column.map((field) => (
                  <div key={field.label}>
                    <label>{field.label}</label>
                    <input
                      name={field.name}
                      onChange={handleChange}
                      value={currentUser[field.name]}
                      className={`input dark ${getInputClassName(
                        field.name as keyof FormValidity
                      )}`}
                      onBlur={(e) => {
                        if (field.name === "email") {
                          validateEmail(e);
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div>
            <button
              className="secondaryButton"
              type="submit"
              disabled={!isFormValid() || isLoading}
              onClick={() => captureEvent("Spara")}
            >
              Spara
            </button>
            {!isFormValid() && (
              <Icon name="warning" size="lg" className="errorIcon" />
            )}
            {saveSuccess()}
          </div>
        </form>
      )}
    </div>
  );
}
